import type { Role, Transition } from '@servicetim/shared';
import {
  Badge,
  Button,
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@servicetim/ui/components';
import { useEffect, useId, useState } from 'react';
import type { FieldCatalog } from '../../projects/catalog/field-catalog';
import { JsonView } from '../../projects/ui/json-view';
import { ConditionBuilder } from '../rules/condition-builder';
import { PostFunctionsEditor } from '../rules/post-functions-editor';
import { ValidatorsEditor } from '../rules/validators-editor';

export interface RuleDrawerProps {
  transition: Transition | null;
  catalog: FieldCatalog;
  roles: readonly Role[];
  projectId: string;
  onApply: (transition: Transition) => void;
  onOpenChange: (open: boolean) => void;
}

type Rules = Pick<Transition, 'conditions' | 'validators' | 'postFunctions'>;

/** Conditions, validators and post-functions of one transition; Apply hands back the transition. */
export function RuleDrawer({
  transition,
  catalog,
  roles,
  projectId,
  onApply,
  onOpenChange,
}: RuleDrawerProps) {
  const id = useId();
  const [rules, setRules] = useState<Rules>({
    conditions: { all: [] },
    validators: [],
    postFunctions: [],
  });
  const [showJson, setShowJson] = useState(false);
  useEffect(() => {
    if (transition) {
      setRules({
        conditions: transition.conditions,
        validators: transition.validators,
        postFunctions: transition.postFunctions,
      });
    }
  }, [transition]);

  return (
    <Sheet open={transition !== null} onOpenChange={onOpenChange}>
      <SheetContent className="overflow-y-auto sm:max-w-2xl">
        <SheetHeader>
          <SheetTitle className="flex flex-wrap items-center gap-2">
            Rules of {transition?.name}
            {transition ? (
              <Badge variant="outline" className="font-mono">
                {transition.key}
              </Badge>
            ) : null}
          </SheetTitle>
          <SheetDescription>
            Conditions decide who sees the transition, validators what must be true to execute it,
            post-functions what happens afterwards. Applying changes the draft; save the draft to
            persist.
          </SheetDescription>
        </SheetHeader>
        {transition ? (
          <div className="flex-1 space-y-4">
            <Tabs defaultValue="conditions">
              <TabsList>
                <TabsTrigger value="conditions">Conditions</TabsTrigger>
                <TabsTrigger value="validators">
                  Validators
                  {rules.validators.length > 0 ? (
                    <Badge variant="secondary">{rules.validators.length}</Badge>
                  ) : null}
                </TabsTrigger>
                <TabsTrigger value="postFunctions">
                  Post-functions
                  {rules.postFunctions.length > 0 ? (
                    <Badge variant="secondary">{rules.postFunctions.length}</Badge>
                  ) : null}
                </TabsTrigger>
              </TabsList>
              <TabsContent value="conditions">
                <p className="mb-2 text-fg-muted text-xs">
                  The caller always needs <code className="font-mono">ticket.transition</code> on
                  the ticket. Conditions narrow further; an empty "All of" means always allowed.
                </p>
                <ConditionBuilder
                  idPrefix={`${id}-cond`}
                  value={rules.conditions}
                  onChange={(conditions) => setRules((r) => ({ ...r, conditions }))}
                  catalog={catalog}
                  roles={roles}
                />
              </TabsContent>
              <TabsContent value="validators">
                <ValidatorsEditor
                  idPrefix={`${id}-val`}
                  value={rules.validators}
                  onChange={(validators) => setRules((r) => ({ ...r, validators }))}
                  catalog={catalog}
                  roles={roles}
                />
              </TabsContent>
              <TabsContent value="postFunctions">
                <PostFunctionsEditor
                  idPrefix={`${id}-pf`}
                  value={rules.postFunctions}
                  onChange={(postFunctions) => setRules((r) => ({ ...r, postFunctions }))}
                  catalog={catalog}
                  projectId={projectId}
                />
              </TabsContent>
            </Tabs>
            <div className="space-y-2">
              <Button
                type="button"
                variant="link"
                size="sm"
                className="px-0"
                onClick={() => setShowJson((v) => !v)}
                aria-expanded={showJson}
              >
                {showJson ? 'Hide rule JSON' : 'Show rule JSON'}
              </Button>
              {showJson ? <JsonView value={rules} className="max-h-64" /> : null}
            </div>
            <SheetFooter className="flex-row justify-end">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="button" onClick={() => onApply({ ...transition, ...rules })}>
                Apply rules
              </Button>
            </SheetFooter>
          </div>
        ) : null}
      </SheetContent>
    </Sheet>
  );
}
